import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import Navbar from "@/components/dibs_landing_page/Navbar";
import Footer from "@/components/dibs_landing_page/Footer";
import { sanityClient, postBySlugQuery, urlForImage, CATEGORY_LABELS, type Post, type SanityImage } from "@/lib/sanity";
import NotFound from "@/pages/NotFound";

const WORDS_PER_MINUTE = 200;

const portableTextToPlain = (blocks?: PortableTextBlock[]): string => {
  if (!blocks?.length) return "";
  return blocks
    .filter((b) => b._type === "block")
    .map((block) => {
      const children = (block as { children?: { text?: string }[] }).children;
      return children?.map((c) => c.text ?? "").join("") ?? "";
    })
    .join(" ");
};

const readingTimeMinutes = (post: Post): number => {
  const text = [post.excerpt ?? "", portableTextToPlain(post.body)].join(" ").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

type TocItem = { id: string; text: string; level: 2 | 3 };

const extractToc = (blocks?: PortableTextBlock[]): TocItem[] => {
  if (!blocks?.length) return [];
  const seen = new Set<string>();
  const items: TocItem[] = [];
  for (const block of blocks) {
    if (block._type !== "block") continue;
    const style = (block as { style?: string }).style;
    if (style !== "h2" && style !== "h3") continue;
    const children = (block as { children?: { text?: string }[] }).children;
    const text = children?.map((c) => c.text ?? "").join("").trim();
    if (!text) continue;
    let id = slugify(text);
    let n = 2;
    while (seen.has(id)) id = `${slugify(text)}-${n++}`;
    seen.add(id);
    items.push({ id, text, level: style === "h2" ? 2 : 3 });
  }
  return items;
};

// Render children with soft-break ("\n") support. Splits text spans on newlines
// and inserts <br/>. Preserves marks/decorators by recursing on children.
const renderWithBreaks = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === "string") {
    const parts = children.split("\n");
    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {part}
      </React.Fragment>
    ));
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => <React.Fragment key={i}>{renderWithBreaks(c)}</React.Fragment>);
  }
  return children;
};

const makeComponents = (headingIds: Map<PortableTextBlock, string>): PortableTextComponents => ({
  types: {
    image: ({ value }: { value: SanityImage }) => (
      <img
        src={urlForImage(value).width(1200).fit("max").url()}
        alt={value.alt ?? ""}
        className="rounded-lg my-8 w-full"
        loading="lazy"
      />
    ),
  },
  block: {
    normal: ({ children, value }) => {
      const isInList = !!(value as { listItem?: string }).listItem;
      if (isInList) return <>{renderWithBreaks(children)}</>;
      return <p className="my-5 leading-relaxed">{renderWithBreaks(children)}</p>;
    },
    h2: ({ children, value }) => {
      const id = headingIds.get(value as PortableTextBlock);
      return (
        <h2 id={id} className="scroll-mt-28 mt-12 mb-4 text-2xl sm:text-3xl font-heading font-bold tracking-tight">
          {children}
        </h2>
      );
    },
    h3: ({ children, value }) => {
      const id = headingIds.get(value as PortableTextBlock);
      return (
        <h3 id={id} className="scroll-mt-28 mt-8 mb-3 text-xl font-heading font-semibold tracking-tight">
          {children}
        </h3>
      );
    },
    blockquote: ({ children }) => (
      <blockquote className="my-6 border-l-4 border-primary/30 pl-4 italic text-muted-foreground">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-5 list-disc pl-6 space-y-2 marker:text-muted-foreground">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="my-5 list-decimal pl-6 space-y-2 marker:text-muted-foreground">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed pl-1">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed pl-1">{children}</li>,
  },
  marks: {
    link: ({ value, children }) => {
      const raw: string = (value?.href ?? "").trim();
      // Normalize: bare domain like "www.usedibs.com" → "https://www.usedibs.com"
      const isAbsolute = /^[a-z]+:\/\//i.test(raw);
      const looksLikeDomain = /^(?:www\.|[a-z0-9-]+\.[a-z]{2,})/i.test(raw);
      const href = !raw
        ? "#"
        : isAbsolute || raw.startsWith("/") || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:")
          ? raw
          : looksLikeDomain
            ? `https://${raw}`
            : raw;
      const external = /^https?:\/\//i.test(href) && !/usedibs\.com/i.test(href);
      return (
        <a
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-primary underline underline-offset-2 decoration-primary/30 hover:decoration-primary transition-colors"
        >
          {children}
        </a>
      );
    },
  },
});

type FetchState =
  | { status: "loading" }
  | { status: "ok"; post: Post }
  | { status: "notfound" }
  | { status: "error"; message: string };

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const articleRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const el = articleRef.current;
      if (!el) return setProgress(0);
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = rect.height - viewportH;
      if (total <= 0) {
        setProgress(rect.bottom <= viewportH ? 1 : 0);
        return;
      }
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(scrolled / total);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [state]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!slug) return;
    sanityClient
      .fetch<Post | null>(postBySlugQuery, { slug })
      .then((post) => setState(post ? { status: "ok", post } : { status: "notfound" }))
      .catch((e) => setState({ status: "error", message: e.message ?? "Failed to load post" }));
  }, [slug]);

  const post = state.status === "ok" ? state.post : null;
  const metaTitle = post?.seo?.metaTitle ?? post?.title;
  const metaDesc = post?.seo?.metaDescription ?? post?.excerpt;
  const ogImage = post?.seo?.ogImage ?? post?.mainImage;
  const postUrl = `https://www.usedibs.com/blog/${slug}`;
  const ogImageUrl = ogImage ? urlForImage(ogImage).width(1200).height(630).fit("crop").url() : undefined;
  const articleJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: metaTitle,
    description: metaDesc,
    image: ogImageUrl,
    datePublished: post.publishedAt,
    mainEntityOfPage: postUrl,
    author: post.author ? { "@type": "Person", name: post.author } : { "@type": "Organization", name: "Dibs" },
    publisher: {
      "@type": "Organization",
      name: "Dibs",
      logo: { "@type": "ImageObject", url: "https://www.usedibs.com/dibs_assets/red_logo_with_transparent_bg.png" },
    },
  };
  const minutes = post ? readingTimeMinutes(post) : 0;
  const categoryLabel = post?.category ? CATEGORY_LABELS[post.category] : null;

  const toc = useMemo(() => (post ? extractToc(post.body) : []), [post]);
  const components = useMemo(() => {
    const headingIds = new Map<PortableTextBlock, string>();
    if (post?.body?.length && toc.length) {
      let i = 0;
      for (const block of post.body) {
        if (block._type !== "block") continue;
        const style = (block as { style?: string }).style;
        if (style === "h2" || style === "h3") {
          const item = toc[i++];
          if (item) headingIds.set(block, item.id);
        }
      }
    }
    return makeComponents(headingIds);
  }, [post, toc]);

  if (state.status === "notfound") return <NotFound />;

  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="text-sm">
      <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
        Blog
      </Link>
      {categoryLabel && (
        <>
          <span className="text-muted-foreground/50 mx-2">/</span>
          <span className="text-foreground font-medium">{categoryLabel}</span>
        </>
      )}
    </nav>
  );

  const authorBlock = post?.author ? (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
        Authored by
      </p>
      <p className="text-sm font-semibold text-foreground">{post.author}</p>
    </div>
  ) : null;

  const tocBlock =
    toc.length > 0 ? (
      <nav aria-label="Table of contents">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
          On this page
        </p>
        <ul className="text-sm">
          {toc.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`block py-1.5 border-l border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors ${
                  item.level === 3 ? "pl-7" : "pl-4"
                }`}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    ) : null;

  return (
    <div className="min-h-screen bg-background">
      {post && (
        <Helmet>
          <title>{`${metaTitle} — Dibs Blog`}</title>
          {metaDesc && <meta name="description" content={metaDesc} />}
          <meta property="og:title" content={metaTitle ?? ""} />
          <meta property="og:type" content="article" />
          {metaDesc && <meta property="og:description" content={metaDesc} />}
          {ogImageUrl && (
            <meta property="og:image" content={ogImageUrl} />
          )}
          <meta property="article:published_time" content={post.publishedAt} />
          {post.author && <meta property="article:author" content={post.author} />}
          <link rel="canonical" href={postUrl} />
          <meta property="og:url" content={postUrl} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle ?? ""} />
          {metaDesc && <meta name="twitter:description" content={metaDesc} />}
          {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
          <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        </Helmet>
      )}

      <Navbar />

      {post && (
        <div
          aria-hidden="true"
          className="fixed left-0 right-0 top-0 z-[60] h-1 bg-border/40 pointer-events-none"
        >
          <div
            className="h-full bg-primary origin-left transition-transform duration-75 ease-out"
            style={{ transform: `scaleX(${progress})`, width: "100%" }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            aria-label="Reading progress"
          />
        </div>
      )}

      <article ref={articleRef} className="pt-24 sm:pt-28 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {state.status === "loading" && (
            <p className="text-muted-foreground text-sm">Loading…</p>
          )}
          {state.status === "error" && (
            <p className="text-destructive text-sm">{state.message}</p>
          )}

          {post && (
            <div className="lg:grid lg:grid-cols-12 lg:gap-10">
              {/* Left aside (sticky on desktop) */}
              <aside className="lg:col-span-3 mb-8 lg:mb-0">
                <div className="lg:sticky lg:top-28 space-y-8">
                  {breadcrumb}

                  {/* On desktop, author + TOC live in the sticky aside.
                      On mobile they move below the cover image (see main content). */}
                  <div className="hidden lg:block space-y-8">
                    {authorBlock}
                    {tocBlock}
                  </div>
                </div>
              </aside>

              {/* Main content */}
              <div className="lg:col-span-9 max-w-3xl">
                <p className="text-sm text-muted-foreground mb-4">
                  {formatDate(post.publishedAt)}
                  <span className="text-muted-foreground/80 mx-2">·</span>
                  {minutes} min read
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight leading-tight mb-8">
                  {post.title}
                </h1>

                {post.excerpt && (
                  <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-10">
                    {post.excerpt}
                  </p>
                )}

                {post.mainImage && (
                  <figure className="mb-10 sm:mb-12">
                    <img
                      src={urlForImage(post.mainImage).width(1600).fit("max").url()}
                      alt={post.mainImage.alt ?? ""}
                      className="rounded-xl w-full shadow-card border border-border/40"
                    />
                  </figure>
                )}

                {(authorBlock || tocBlock) && (
                  <div className="lg:hidden space-y-8 mb-10 sm:mb-12">
                    {authorBlock}
                    {tocBlock}
                  </div>
                )}

                <div className="prose prose-neutral dark:prose-invert prose-headings:font-heading prose-headings:tracking-tight prose-a:text-primary max-w-none text-[15px] sm:text-base leading-relaxed">
                  <PortableText value={post.body} components={components} />
                </div>
              </div>
            </div>
          )}
        </div>
      </article>

      {/* The footer card overlaps the content above it by 88px, so add that on top of the visible gap. */}
      <div className="pb-[152px] sm:pb-[184px]" />

      <Footer />
    </div>
  );
};

export default BlogPost;
